// RecurringInvoicesView — define rules that auto-generate invoices on a
// schedule (monthly retainers, recurring rent, etc). Generated invoices
// land as DRAFT in the Invoices view (backend-side) so the agent can
// review and send.

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Pause, Play, Zap, Clock, X, AlertCircle,
  Receipt, Loader2, RefreshCw, Repeat,
} from 'lucide-react';
import {
  listRecurringInvoices, createRecurringInvoice, updateRecurringInvoice,
  deleteRecurringInvoice, pauseRecurringInvoice, resumeRecurringInvoice, runRecurringInvoiceNow,
} from '../api.js';
import { toast } from './Toast.jsx';
import { confirmDialog } from './ui.jsx';

const FREQUENCY_OPTS = [
  { value: 'DAILY',   labelEn: 'Daily',   labelEs: 'Diaria' },
  { value: 'WEEKLY',  labelEn: 'Weekly',  labelEs: 'Semanal' },
  { value: 'MONTHLY', labelEn: 'Monthly', labelEs: 'Mensual' },
  { value: 'YEARLY',  labelEn: 'Yearly',  labelEs: 'Anual' },
];

const DOW = [
  { v: 0, en: 'Sun', es: 'Dom' }, { v: 1, en: 'Mon', es: 'Lun' },
  { v: 2, en: 'Tue', es: 'Mar' }, { v: 3, en: 'Wed', es: 'Mié' },
  { v: 4, en: 'Thu', es: 'Jue' }, { v: 5, en: 'Fri', es: 'Vie' },
  { v: 6, en: 'Sat', es: 'Sáb' },
];

function fmtDate(d, lang) {
  if (!d) return '--';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '--';
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

function describeRule(r, lang) {
  const es = lang === 'es';
  const interval = r.intervalCount > 1 ? `${es ? 'cada' : 'every'} ${r.intervalCount} ` : '';
  const freq = (FREQUENCY_OPTS.find(f => f.value === r.frequency) || {});
  const freqLbl = es ? freq.labelEs : freq.labelEn;
  const time = r.timeOfDay ? ` @ ${r.timeOfDay}` : '';
  let extra = '';
  if (r.frequency === 'WEEKLY' && r.dayOfWeek != null) {
    const d = DOW.find(x => x.v === r.dayOfWeek);
    extra = ` (${es ? d?.es : d?.en})`;
  } else if (r.frequency === 'MONTHLY' && r.dayOfMonth) {
    extra = ` (${es ? 'día' : 'day'} ${r.dayOfMonth})`;
  }
  return `${interval}${freqLbl}${extra}${time}`;
}

export function RecurringInvoicesView({ clients = [], lang = 'en' }) {
  const es = lang === 'es';
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listRecurringInvoices();
      setRules(list);
    } catch (err) {
      toast.error((es ? 'Error: ' : 'Error: ') + err.message);
    } finally {
      setLoading(false);
    }
  }, [es]);

  useEffect(() => { refresh(); }, [refresh]);

  // Sidebar nav reset
  useEffect(() => {
    const h = (e) => { if (e?.detail?.view === 'recurring_invoices') { setShowForm(false); setEditing(null); } };
    window.addEventListener('deskte:nav-reset', h);
    return () => window.removeEventListener('deskte:nav-reset', h);
  }, []);

  const onPause = async (id) => {
    try { await pauseRecurringInvoice(id); await refresh(); toast.success(es ? 'Pausada' : 'Paused'); }
    catch (e) { toast.error(e.message); }
  };
  const onResume = async (id) => {
    try { await resumeRecurringInvoice(id); await refresh(); toast.success(es ? 'Reanudada' : 'Resumed'); }
    catch (e) { toast.error(e.message); }
  };
  const onDelete = async (id) => {
    if (!(await confirmDialog({ message: es ? '¿Eliminar esta regla recurrente?' : 'Delete this recurring rule?', danger: true }))) return;
    try { await deleteRecurringInvoice(id); await refresh(); toast.success(es ? 'Eliminada' : 'Deleted'); }
    catch (e) { toast.error(e.message); }
  };
  const onRunNow = async (id) => {
    try {
      const res = await runRecurringInvoiceNow(id);
      toast.success(es
        ? `Factura DRAFT creada. Revisa Invoices.`
        : `Draft invoice created. Check Invoices.`);
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  const totalPerPeriod = useMemo(() => rules.filter(r => r.isActive).reduce((s, r) => {
    const items = Array.isArray(r.items) ? r.items : [];
    const subtotal = items.reduce((acc, it) => acc + Number(it.qty || 1) * Number(it.rate || 0), 0);
    const disc = r.discountType === 'percent' ? subtotal * (Number(r.discount || 0) / 100) : Number(r.discount || 0);
    const afterDisc = Math.max(0, subtotal - disc);
    const tax = afterDisc * (Number(r.taxRate || 0) / 100);
    return s + afterDisc + tax;
  }, 0), [rules]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div className="dkt-sec-title" style={{ margin: 0 }}>
            <Repeat size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            {es ? 'Facturas Recurrentes' : 'Recurring Invoices'}
          </div>
          <div className="dkt-sec-sub">{es ? 'Auto-generar facturas mensuales para retainers, renta, etc.' : 'Auto-generate monthly invoices for retainers, rent, etc.'}</div>
        </div>
        <button className="dkt-btn-p" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={14} /> {es ? 'Nueva regla' : 'New rule'}
        </button>
      </div>

      <div className="dkt-card" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            {es ? 'Reglas activas' : 'Active rules'}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-cyan,#00d4ff)' }}>{rules.filter(r => r.isActive).length}</div>
        </div>
        <div style={{ width: 1, height: 40, background: 'var(--c-border,#1a3550)' }} />
        <div>
          <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            {es ? 'Total por período' : 'Total per period'}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-green,#00ff88)' }}>{fmtMoney(totalPerPeriod)}</div>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)' }}><Loader2 size={20} className="animate-spin" /></div>}

      {!loading && rules.length === 0 && (
        <div className="dkt-card" style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--c-text3,#45688a)' }}>
          <Repeat size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: '.9rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: 4 }}>
            {es ? 'Sin reglas recurrentes' : 'No recurring rules'}
          </div>
          <div style={{ fontSize: '.78rem' }}>
            {es ? 'Crea una para auto-generar facturas en horario fijo.' : 'Create one to auto-generate invoices on a schedule.'}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        {rules.map((r) => {
          const items = Array.isArray(r.items) ? r.items : [];
          const subtotal = items.reduce((acc, it) => acc + Number(it.qty || 1) * Number(it.rate || 0), 0);
          return (
            <div key={r.id} className="dkt-card" style={{ padding: '.85rem 1rem', borderLeft: `3px solid ${r.isActive ? 'var(--c-green,#00ff88)' : 'var(--c-text3,#45688a)'}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.6rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--c-white,#eef6ff)', marginBottom: 2 }}>
                    {r.title}
                    {!r.isActive && <span style={{ marginLeft: 8, fontSize: '.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--ci-bd-15)', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{es ? 'PAUSADA' : 'PAUSED'}</span>}
                  </div>
                  <div style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: 4 }}>
                    👤 {r.client?.firstName} {r.client?.lastName}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
                    <span>🔄 {describeRule(r, lang)}</span>
                    <span>·</span>
                    <span>💵 {fmtMoney(subtotal)}</span>
                    <span>·</span>
                    <span>📅 {es ? 'Próxima' : 'Next'}: {fmtDate(r.nextRunAt, lang)}</span>
                    {r.lastRunAt && <><span>·</span><span>{es ? 'Última' : 'Last'}: {fmtDate(r.lastRunAt, lang)}</span></>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '.3rem', flexShrink: 0 }}>
                  <button onClick={() => onRunNow(r.id)} className="dkt-btn-g" title={es ? 'Generar ahora' : 'Run now'}>
                    <Zap size={12} />
                  </button>
                  {r.isActive ? (
                    <button onClick={() => onPause(r.id)} className="dkt-btn-g" title={es ? 'Pausar' : 'Pause'}>
                      <Pause size={12} />
                    </button>
                  ) : (
                    <button onClick={() => onResume(r.id)} className="dkt-btn-g" title={es ? 'Reanudar' : 'Resume'}>
                      <Play size={12} />
                    </button>
                  )}
                  <button onClick={() => { setEditing(r); setShowForm(true); }} className="dkt-btn-g" title={es ? 'Editar' : 'Edit'}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={() => onDelete(r.id)} className="dkt-btn-d" style={{ padding: '.38rem .55rem' }} title={es ? 'Eliminar' : 'Delete'}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <RecurringInvoiceForm
          rule={editing}
          clients={clients}
          lang={lang}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
}

function RecurringInvoiceForm({ rule, clients, lang, onClose, onSaved }) {
  const es = lang === 'es';
  const isEdit = !!rule;
  const [title, setTitle] = useState(rule?.title || '');
  const [invoiceTitle, setInvoiceTitle] = useState(rule?.invoiceTitle || '');
  const [clientId, setClientId] = useState(rule?.clientId || (clients[0]?.id || ''));
  const [frequency, setFrequency] = useState(rule?.frequency || 'MONTHLY');
  const [intervalCount, setIntervalCount] = useState(rule?.intervalCount || 1);
  const [dayOfWeek, setDayOfWeek] = useState(rule?.dayOfWeek ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(rule?.dayOfMonth ?? 1);
  const [timeOfDay, setTimeOfDay] = useState(rule?.timeOfDay || '09:00');
  const [netDays, setNetDays] = useState(rule?.netDays ?? 14);
  const [taxRate, setTaxRate] = useState(rule?.taxRate ?? 0);
  const [items, setItems] = useState(() => {
    const fromRule = Array.isArray(rule?.items) ? rule.items : null;
    return fromRule && fromRule.length > 0
      ? fromRule.map(it => ({ description: it.description || '', qty: Number(it.qty || 1), rate: Number(it.rate || 0) }))
      : [{ description: '', qty: 1, rate: 0 }];
  });
  const [notes, setNotes] = useState(rule?.notes || '');
  const [saving, setSaving] = useState(false);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { description: '', qty: 1, rate: 0 }]);
  const removeItem = (idx) => setItems((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const save = async () => {
    if (!title.trim()) { toast.warn(es ? 'Título requerido' : 'Title required'); return; }
    if (!clientId) { toast.warn(es ? 'Selecciona un cliente' : 'Pick a client'); return; }
    const validItems = items.filter(it => it.description.trim() && Number(it.rate) > 0);
    if (validItems.length === 0) { toast.warn(es ? 'Agrega al menos un ítem con monto' : 'Add at least one item with amount'); return; }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        invoiceTitle: invoiceTitle.trim() || null,
        clientId,
        frequency,
        intervalCount: Math.max(1, parseInt(intervalCount, 10) || 1),
        dayOfWeek: frequency === 'WEEKLY' ? Number(dayOfWeek) : null,
        dayOfMonth: frequency === 'MONTHLY' ? Math.min(28, Math.max(1, Number(dayOfMonth))) : null,
        timeOfDay,
        netDays: Math.max(0, Number(netDays) || 14),
        taxRate: Number(taxRate) || 0,
        items: validItems,
        notes: notes.trim() || null,
      };
      if (isEdit) await updateRecurringInvoice(rule.id, payload);
      else await createRecurringInvoice(payload);
      toast.success(isEdit ? (es ? 'Actualizada' : 'Updated') : (es ? 'Creada' : 'Created'));
      await onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const totalPreview = items.reduce((s, it) => s + Number(it.qty || 1) * Number(it.rate || 0), 0);
  const taxAmt = totalPreview * (Number(taxRate || 0) / 100);

  return (
    <div className="dkt-overlay" onClick={onClose}>
      <div className="dkt-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div className="dkt-mhdr">
          <span className="dkt-mtitle">{isEdit ? (es ? 'Editar regla' : 'Edit rule') : (es ? 'Nueva regla recurrente' : 'New recurring rule')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}><X size={16} /></button>
        </div>
        <div className="dkt-mbody" style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
          <div>
            <label className="dkt-input-lbl">{es ? 'Nombre interno' : 'Internal name'}</label>
            <input className="dkt-inp" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={es ? 'Ej: Renta oficina mensual' : 'e.g. Monthly office rent'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
            <div>
              <label className="dkt-input-lbl">{es ? 'Cliente' : 'Client'}</label>
              <select className="dkt-inp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">--</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
              </select>
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Título de la factura' : 'Invoice title'}</label>
              <input className="dkt-inp" value={invoiceTitle} onChange={(e) => setInvoiceTitle(e.target.value)} placeholder={es ? '(usa nombre interno si vacío)' : '(uses internal name if empty)'} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.75rem' }}>
            <div>
              <label className="dkt-input-lbl">{es ? 'Frecuencia' : 'Frequency'}</label>
              <select className="dkt-inp" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                {FREQUENCY_OPTS.map(f => <option key={f.value} value={f.value}>{es ? f.labelEs : f.labelEn}</option>)}
              </select>
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Cada' : 'Every'}</label>
              <input className="dkt-inp" type="number" min="1" value={intervalCount} onChange={(e) => setIntervalCount(e.target.value)} />
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Hora' : 'Time of day'}</label>
              <input className="dkt-inp" type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
            </div>
          </div>

          {frequency === 'WEEKLY' && (
            <div>
              <label className="dkt-input-lbl">{es ? 'Día de la semana' : 'Day of week'}</label>
              <select className="dkt-inp" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
                {DOW.map(d => <option key={d.v} value={d.v}>{es ? d.es : d.en}</option>)}
              </select>
            </div>
          )}

          {frequency === 'MONTHLY' && (
            <div>
              <label className="dkt-input-lbl">{es ? 'Día del mes (1-28)' : 'Day of month (1-28)'}</label>
              <input className="dkt-inp" type="number" min="1" max="28" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} />
            </div>
          )}

          <div>
            <label className="dkt-input-lbl">{es ? 'Ítems' : 'Line items'}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 32px', gap: '.4rem', alignItems: 'center' }}>
                  <input className="dkt-inp" placeholder={es ? 'Descripción' : 'Description'} value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} />
                  <input className="dkt-inp" type="number" min="0" step="0.01" placeholder={es ? 'Cant' : 'Qty'} value={it.qty} onChange={(e) => setItem(idx, { qty: Number(e.target.value) })} />
                  <input className="dkt-inp" type="number" min="0" step="0.01" placeholder={es ? 'Precio' : 'Rate'} value={it.rate} onChange={(e) => setItem(idx, { rate: Number(e.target.value) })} />
                  <button onClick={() => removeItem(idx)} className="dkt-btn-d" style={{ padding: '.5rem' }} disabled={items.length === 1}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addItem} className="dkt-btn-g" style={{ marginTop: 4 }}><Plus size={11} /> {es ? 'Agregar ítem' : 'Add item'}</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
            <div>
              <label className="dkt-input-lbl">{es ? 'Net (días para vencer)' : 'Net (days to due)'}</label>
              <input className="dkt-inp" type="number" min="0" value={netDays} onChange={(e) => setNetDays(e.target.value)} />
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Impuesto (%)' : 'Tax rate (%)'}</label>
              <input className="dkt-inp" type="number" min="0" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="dkt-input-lbl">{es ? 'Notas' : 'Notes'}</label>
            <textarea className="dkt-inp" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div style={{ background: 'var(--c-surface2,#112538)', padding: '.75rem 1rem', borderRadius: 9, border: '1px solid var(--c-border,#1a3550)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: 'var(--c-text2,#7aa3c0)' }}>
              <span>{es ? 'Subtotal' : 'Subtotal'}</span><span>{fmtMoney(totalPreview)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: 'var(--c-text2,#7aa3c0)' }}>
              <span>{es ? 'Impuesto' : 'Tax'}</span><span>{fmtMoney(taxAmt)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.85rem', fontWeight: 800, color: 'var(--c-cyan,#00d4ff)', marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--c-border,#1a3550)' }}>
              <span>{es ? 'Total / período' : 'Total / period'}</span><span>{fmtMoney(totalPreview + taxAmt)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="dkt-btn-s" disabled={saving}>{es ? 'Cancelar' : 'Cancel'}</button>
            <button onClick={save} className="dkt-btn-p" disabled={saving}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : (es ? 'Guardar' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
